import React, {
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  useCallback,
} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { debounce, isEmpty } from "lodash";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PlusCircleIcon } from "@heroicons/react/20/solid";
import { cn } from "@/lib/utils";
import toast from "react-hot-toast";
import {
  getTemplates,
  deleteTemplate,
  getAdminTemplates,
  getShareTemplatesWithMe,
} from "../utils/Services";
import useTemplateStore from "@/utils/TemplateStore";
import Template from "../components/Template";
import EmptyNotes from "./EmptyNotes";
import { SUBSCRIPTION_STATUS, USER_ROLES } from "@/utils/constants";
import { useAppSelector } from "../hooks";
import { ITemplate } from "../utils/Types";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SortDescending,
  SortAscending,
  FunnelX,
  Funnel,
  FileText,
  Stethoscope,
  Heartbeat,
  Thermometer,
  Brain,
  Users,
  Syringe,
  FirstAidKit,
  Pill,
  FunnelSimple,
} from "@phosphor-icons/react";
import TransparentModal from "@/components/TransparentModal";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import useUnsavedChangesStore from "@/utils/UnsavedChangesStore";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import useIsMobile from "@/hooks/useIsMobile";

const Templates: React.FC = () => {
  const isLoading = useRef(false);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(true);
  const [inputText, setInputText] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const [templatesCount, setTemplatesCount] = useState(0);
  const user = useAppSelector((state) => state.user.user);
  const sortBy = useRef("created");
  const filterBy = useRef("");
  const [orderBy, setOrderBy] = useState("-");
  const [isFilter, setIsFilter] = useState(false);
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [isShowAdd, setShowAdd] = useState(false);
  const { templateStore, addCategory, ctgStore } = useTemplateStore();
  const [open, setOpen] = useState(false);
  const [refetchTemplates, setRefetchTemplates] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const {
    pendingNavigation,
    setPendingNavigation,
    unsavedChanges,
    setUnsavedChanges,
  } = useUnsavedChangesStore();
  // const { version, setVersion } = useTemplateVersionSelectorStore();
  // const toggleActive = useRef(false);
  // const isMobile = useIsMobile();
  // const { unsavedChanges, setUnsavedChanges } =
  //   useTemplateUnsavedChangesStore();

  const [textkey, setTextkey] = useState("");
  const { height, width } = useWindowDimensions();

  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isMobile = useIsMobile();

  const SORTS = [
    {
      text: "Title",
      value: "title",
    },
    { text: "Time", value: "created_at" },
  ];

  const categoryOptions = [
    {
      key: null,
      text: "Filter by category",
      icon: null,
    },
    {
      key: "history_present_illness",
      text: "History of Present Illness",
      icon: <FileText size={17} />,
    },
    {
      key: "review_of_systems",
      text: "Review of Systems",
      icon: <Stethoscope size={17} />,
    },
    {
      key: "physical_exam",
      text: "Physical Exam",
      icon: <Heartbeat size={17} />,
    },
    {
      key: "differential_diagnosis",
      text: "Differential Diagnosis",
      icon: <Thermometer size={17} />,
    },
    {
      key: "medical_decision_making",
      text: "Medical Decision Making",
      icon: <Brain size={17} />,
    },
    {
      key: "consults",
      text: "Consults",
      icon: <Users size={17} />,
    },
    {
      key: "procedures",
      text: "Procedures",
      icon: <Syringe size={17} />,
    },
    {
      key: "critical_care_note",
      text: "Critical Care Note",
      icon: <FirstAidKit size={17} />,
    },
    {
      key: "disposition",
      text: "Disposition",
      icon: <Pill size={17} />,
    },
  ];

  const options = {
    history_present_illness: {
      text: "History of Present Illness",
      icon: <FileText size={20} />,
    },
    review_of_systems: {
      text: "Review of Systems",
      icon: <Thermometer size={20} />,
    },
    physical_exam: {
      text: "Physical Exam",
      icon: <Stethoscope size={20} />,
    },
    differential_diagnosis: {
      text: "Differential Diagnosis",
      icon: <Heartbeat size={20} />,
    },
    medical_decision_making: {
      text: "Medical Decision Making",
      icon: <Brain size={20} />,
    },
    consults: {
      text: "Consults",
      icon: <Users size={20} />,
    },
    procedures: {
      text: "Procedures",
      icon: <Syringe size={20} />,
    },
    critical_care_note: {
      text: "Critical Care Note",
      icon: <FirstAidKit size={20} />,
    },
    disposition: {
      text: "Disposition",
      icon: <Pill size={20} />,
    },
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    const { ctrlKey, key, target } = event;

    if (
      target instanceof HTMLElement &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
    ) {
      return;
    }

    if (!ctrlKey && key === "/") {
      const el = document.getElementById("search");

      if (el) {
        event.preventDefault();
        el.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const fetchTemplates = async (
    search = "",
    replace: boolean = false,
    order = "-",
    sort = "created",
    off = 0,
    filter = ""
  ) => {
    if (replace) {
      setIsTemplatesLoading(true);
    }

    try {
      let response: {
        count: number;
        results: ITemplate[];
      };
      if (isAdmin) {
        response = await getAdminTemplates({
          ...(search && { search: search }),
          limit: limit,
          offset: off,
          ordering: order + sort,
          ...(filter && { category: filter }),
          ...(isAdmin && { admin_dashboard: isAdmin }),
        });
      } else if (
        window.location.pathname.startsWith("/dashboard/shared-templates")
      ) {
        response = await getShareTemplatesWithMe({
          ...(search && { search: search }),
          limit: limit,
          offset: off,
          ordering: order + sort,
          ...(filter && { category: filter }),
        });
      } else {
        response = await getTemplates({
          ...(search && { search: search }),
          limit: limit,
          offset: off,
          ordering: order + sort,
          ...(filter && { category: filter }),
        });
      }

      if (replace) {
        setTemplates(() => [
          ...(templateId === "create"
            ? [
                {
                  id: "create",
                  title: "New Template",
                  description: "",
                  content: "",
                  category: ctgStore,
                  json_content: {},
                  current_version: "",
                  default: false,
                  is_active: false,
                  updated_at: "",
                  created_at: "",
                },
              ]
            : []),
          ...response?.results,
        ]);
      } else {
        setTemplates((prev) => [...prev, ...response?.results]);
      }
      setTemplatesCount(response?.count);
    } catch (error) {
    } finally {
      setIsTemplatesLoading(false);
    }
  };

  const fetchMoreNotes = () => {
    try {
      setOffset(offset + limit);
      fetchTemplates(
        inputText,
        false,
        orderBy,
        sortBy.current,
        offset + limit,
        filterBy.current
      );
    } catch (error) {}
  };

  useEffect(() => {
    if ("id" in templateStore) {
      const temp = templates.find((temp) => temp.id === templateStore.id);
      if (temp) {
        setTemplates(
          templates.map((template) =>
            template?.id === templateStore.id ? templateStore : template
          )
        );
      } else {
        setTemplates((prev) =>
          prev.map((template) =>
            template?.id === "create" ? templateStore : template
          )
        );
      }
    }
  }, [templateStore]);

  const path = window.location.pathname;

  useEffect(() => {
    if (!templateId) {
      setIsTemplatesLoading(true);
      setOffset(0);
      if (!templates.length) {
        setRefetchTemplates(true);
      } else {
        setTemplates([]);
      }
    }
  }, [sortBy.current, path, orderBy, filterBy.current]);

  useEffect(() => {
    setRefetchTemplates(false);
    const res = setTimeout(async () => {
      if (
        !templates.length &&
        !isEmpty(user) &&
        (user?.is_staff ||
        user?.beta ||
        [
          USER_ROLES.STAFFADMIN,
          USER_ROLES.SUPERADMIN,
          USER_ROLES.ADMIN,
        ].includes(user.role!)
          ? true
          : ![SUBSCRIPTION_STATUS.IN_ACTIVE].includes(
              user?.subscription_status!
            ))
      ) {
        fetchTemplates(
          inputText,
          true,
          orderBy,
          sortBy.current,
          0,
          filterBy.current
        );
      }
    }, 500);

    return () => {
      clearTimeout(res);
    };
  }, [
    user,
    templates.length,
    filterBy.current,
    sortBy.current,
    refetchTemplates,
  ]);

  const debounceFn = useCallback(debounce(fetchTemplates, 500), []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    debounceFn(
      e.target.value,
      true,
      orderBy,
      sortBy.current,
      0,
      filterBy.current
    );
  };

  const addNewTemplate = async (option: string) => {
    addCategory(option);
    isLoading.current = true;
    try {
      setTemplates((prev) => {
        const existingCreateTemplate = prev.find(
          (template) => template.id === "create"
        );

        if (existingCreateTemplate) {
          const updatedTemplate = {
            ...existingCreateTemplate,
            content: "",
            category: option,
          };

          setCurrentCreateTemplate(updatedTemplate);

          return prev.map((template) => {
            if (template.id === "create") {
              return updatedTemplate;
            }
            return template;
          });
        } else {
          const newTemplate = {
            id: "create",
            title: "New Template",
            description: "",
            category: option,
            content: "",
            default: false,
            is_active: false,
            updated_at: "",
            created_at: "",
          };

          // Store the new template for the Outlet context
          setCurrentCreateTemplate(newTemplate);

          return [newTemplate, ...prev];
        }
      });

      navigate(`${isAdmin ? "/admin" : ""}/dashboard/templates/create`);
    } catch (error) {
    } finally {
      isLoading.current = false;
    }
  };

  const navigateNextTemplate = (templates: ITemplate[], noteIndex: number) => {
    const nextNoteIndex = noteIndex + 1;
    const previousNoteIndex = noteIndex - 1;

    if (nextNoteIndex < templates.length) {
      const nextId = templates[nextNoteIndex]?.id?.toString();
      navigate(`${isAdmin ? "/admin" : ""}/dashboard/templates/${nextId}`);
    } else if (previousNoteIndex >= 0) {
      const previousId = templates[previousNoteIndex]?.id?.toString();
      navigate(`${isAdmin ? "/admin" : ""}/dashboard/templates/${previousId}`);
    } else {
      navigate(`${isAdmin ? "/admin" : ""}/dashboard/templates/empty-notes`);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    const tempIndex = templates.findIndex((temp) => temp.id == templateId);

    if (templates.length > 0 && tempIndex !== -1) {
      navigateNextTemplate(templates, tempIndex);
    }
    await deleteTemplate(templateId);
    fetchTemplates("", true);

    toast.success("Template Deleted!", {
      duration: 1000,
      position: "top-center",
    });
  };

  const showAdd = () => {
    const show = templates[0]?.id === "create" ? false : true;
    setShowAdd(show);
  };

  const onCreateTemplate = () => {
    if (unsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      setOpen(true);
    }
  };

  const [currentCreateTemplate, setCurrentCreateTemplate] =
    useState<ITemplate | null>(null);

  // Reference to access the saveTemplate function from the Template component
  const [saveTemplateFunction, setSaveTemplateFunction] = useState<
    (() => Promise<void>) | null
  >(null);

  return (
    <div
      style={{
        height: `calc(100dvh - 65px)`,
      }}
      className="bg-gray-25/10 dark:bg-gray-900/10"
    >
      <TooltipProvider delayDuration={0}>
        <ResizablePanelGroup direction="horizontal">
          <ResizablePanel
            defaultSize={20}
            collapsedSize={18}
            collapsible={true}
            minSize={18}
            maxSize={50}
            onResize={(collapsed) => {
              setTextkey(collapsed.toString());
            }}
            className={cn(
              isMobile || !templateId
                ? "dark:bg-gray-950"
                : "rounded-lg bg-white dark:bg-gray-950 my-4 ml-4 mr-2 border-[0.5px] dark:border-gray-700 shadow-md",
              templateId ? "" : "flex flex-col items-center",
              width <= 1200 && templateId ? "hidden" : ""
            )}
          >
            <div
              className={
                " " +
                (!templateId && width <= 768
                  ? "w-full"
                  : templateId && width >= 768
                  ? ""
                  : "w-full md:max-w-[807px]")
              }
            >
              {!templateId && width >= 768 && (
                <div className="mt-5 flex items-center h-[32px] px-2">
                  <span className="font-semibold text-xl">Templates</span>

                  <Button
                    disabled={isLoading.current || isTemplatesLoading}
                    onClick={() => onCreateTemplate()}
                    className="ml-auto h-[32px] w-[93px] rounded-full ml-auto flex items-center justify-center bg-sky-200 hover:bg-sky-100 text-white"
                  >
                    {isLoading.current ? (
                      <span className="loader h-5 w-5"></span>
                    ) : (
                      <div className="flex items-center">
                        <div className="h-5 w-5 mr-1">
                          <PlusCircleIcon />
                        </div>
                        <span>Create</span>
                      </div>
                    )}
                  </Button>
                </div>
              )}
              {(templateId || width <= 768) && (
                <div className="flex items-center px-4 border-b-[0.5px] h-[56px] bg-gray-25/20 dark:bg-gray-800/50">
                  <span className="font-semibold text-xl">Templates</span>

                  <Button
                    type="submit"
                    onClick={() => onCreateTemplate()}
                    disabled={isLoading.current || isTemplatesLoading}
                    className="h-[32px] w-[93px] rounded-full ml-auto flex items-center justify-center bg-sky-200 hover:bg-sky-100 text-white"
                  >
                    {isLoading.current ? (
                      <span className="loader h-5 w-5"></span>
                    ) : (
                      <div className="flex items-center">
                        <div className="h-5 w-5 mr-1">
                          <PlusCircleIcon />
                        </div>
                        <div>Create</div>
                      </div>
                    )}
                  </Button>
                </div>
              )}

              <div
                className={
                  templateId || width <= 768
                    ? "w-full px-4"
                    : "flex items-center my-4"
                }
              >
                <div
                  className={
                    templateId || width <= 768
                      ? "mt-5 flex items-center justify-center"
                      : "min-w-[350px] px-2"
                  }
                >
                  <Input
                    id="search"
                    type="search"
                    placeholder="Type / for search."
                    onChange={handleInputChange}
                  />

                  {(!!templateId || width <= 768) && (
                    <Button
                      onClick={() => {
                        setIsFilter(!isFilter);
                      }}
                      variant={"ghost"}
                      className="ml-1 p-2"
                    >
                      {isFilter ? (
                        <FunnelX className="cursor-pointer" size={22} />
                      ) : (
                        <Funnel className="cursor-pointer" size={22} />
                      )}
                    </Button>
                  )}
                </div>

                <div
                  className={
                    "w-full flex items-center justify-center " +
                    (templateId || width <= 768 ? "flex-col my-2 " : "mx-2 ") +
                    (templateId || width <= 768
                      ? isFilter
                        ? "filter-expand "
                        : "filter-contract contract"
                      : "")
                  }
                >
                  <Select
                    onValueChange={(val) => {
                      setIsTemplatesLoading(true);
                      filterBy.current = val;
                      setOffset(0);
                      setTemplates([]);
                    }}
                  >
                    <SelectTrigger className="min-w-[180px] dark:text-gray-50">
                      <SelectValue placeholder="Filter by category" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-gray-800">
                      <SelectGroup>
                        {categoryOptions.map((sort) => {
                          return (
                            <SelectItem
                              showIndicator={false}
                              key={sort.key}
                              className="px-2 hover:bg-gray-25 dark:hover:bg-gray-500"
                              value={sort.key!}
                            >
                              <div
                                className={
                                  "flex items-center " +
                                  (sort.key === null
                                    ? "text-gray-500 dark:text-gray-50"
                                    : "")
                                }
                              >
                                <div className="mr-2">
                                  {sort.key === null ? (
                                    <FunnelSimple size={17} />
                                  ) : (
                                    sort.icon
                                  )}
                                </div>
                                {sort.text}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectGroup>
                    </SelectContent>
                  </Select>

                  <div
                    className={
                      " w-full flex items-center " +
                      (templateId || width <= 768 ? "mt-2 " : "ml-2")
                    }
                  >
                    <div className="w-full mr-1">
                      <Select
                        onValueChange={(val) => {
                          setIsTemplatesLoading(true);
                          sortBy.current = val;
                          setOffset(0);
                          setTemplates([]);
                        }}
                      >
                        <SelectTrigger className="min-w-[180px]">
                          <SelectValue placeholder="Order by" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-800">
                          <SelectGroup>
                            {SORTS.map((sort) => {
                              return (
                                <SelectItem
                                  key={sort.value}
                                  showIndicator={false}
                                  className="px-2 hover:bg-gray-25 dark:hover:bg-gray-500"
                                  value={sort.value}
                                >
                                  {sort.text}
                                </SelectItem>
                              );
                            })}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={() => {
                        setIsTemplatesLoading(true);
                        setOrderBy(orderBy === "" ? "-" : "");
                        setOffset(0);
                        setTemplates([]);
                      }}
                      variant={"ghost"}
                      className="p-2"
                    >
                      {orderBy === "-" ? (
                        <SortDescending className="cursor-pointer" size={22} />
                      ) : (
                        <SortAscending className="cursor-pointer" size={22} />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div
              id="scrollableDiv"
              className="dark:text-white w-full"
              style={
                templateId
                  ? {
                      overflow: "auto",
                      height: `calc(100dvh - ${isFilter ? 270 : 190}px)`,
                    }
                  : { overflow: "auto", height: "calc(100dvh - 130px)" }
              }
            >
              <div
                className={
                  "mx-auto " +
                  (templateId ? "px-2 py-4" : "w-full md:max-w-[807px]")
                }
              >
                {isTemplatesLoading && (
                  <div className="px-2">
                    {[1, 2, 3].map((key) => (
                      <Skeleton
                        key={key}
                        className="bg-gray-25 dark:bg-gray-900 h-[80px] w-full rounded-xl my-2"
                      />
                    ))}
                  </div>
                )}
                {!isTemplatesLoading && templates.length ? (
                  <InfiniteScroll
                    dataLength={templates.length}
                    next={fetchMoreNotes}
                    hasMore={templatesCount > templates.length}
                    loader={
                      <div className="w-full text-center p-2">
                        <span className="loader w-5 h-5"></span>
                      </div>
                    }
                    scrollableTarget="scrollableDiv"
                  >
                    <ul role="list" className="space-y-3 mx-2">
                      {templates.map((template) => (
                        <Template
                          key={template?.id}
                          template={template}
                          // @ts-ignore
                          icon={options[template?.category!]?.icon}
                          // @ts-ignore
                          category={options[template?.category!]?.text}
                        />
                      ))}
                    </ul>
                  </InfiniteScroll>
                ) : (
                  <>
                    {!isTemplatesLoading && (
                      <div className="h-full flex items-center">
                        <EmptyNotes description="No Templates Found" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </ResizablePanel>
          {templateId && (
            <>
              {width >= 768 && (
                <div className="flex items-center">
                  <ResizableHandle withHandle />
                </div>
              )}
              <ResizablePanel
                defaultSize={60}
                style={{
                  overflow: "auto",
                  height: "100%",
                }}
              >
                <Outlet
                  context={{
                    handleDeleteTemplate,
                    currentCreateTemplate,
                    setSaveTemplateFunction, // Pass the setter to the Template component
                  }}
                />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </TooltipProvider>
      <TransparentModal
        handleSelect={(option) => addNewTemplate(option)}
        open={open}
        setOpen={setOpen}
      />
      <UnsavedChangesDialog
        showUnsavedDialog={showUnsavedDialog}
        setShowUnsavedDialog={setShowUnsavedDialog}
        confirmNavigation={async () => {
          if (saveTemplateFunction) {
            await saveTemplateFunction();
          }
          setShowUnsavedDialog(false);
          setOpen(true);
        }}
        cancelNavigation={() => {
          setShowUnsavedDialog(false);
        }}
        discardChanges={() => {
          setUnsavedChanges(false);
          setShowUnsavedDialog(false);
          setOpen(true);
        }}
      />
    </div>
  );
};

export default Templates;
